const carouselArrow = (prev = false) => `
	<button disabled class="arrow arrow--${prev ? 'prev' : 'next' }" aria-label="${prev ? 'Previous' : 'Next'}">
			<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M26.1201 43.76L37.8801 32L26.1201 20.24" stroke="#212121" stroke-width="1.28"/>
			</svg>
	</button>
`;

class CarouselV2 extends HTMLElement {
	constructor() {
		super();

		this.arrows = this.hasAttribute('arrows');
		this.events = this.hasAttribute('events');
		this.infinite = this.hasAttribute('infinite');
		this.autoplay = this.hasAttribute('autoplay');

		const shadow = this.attachShadow({ mode: 'open' });
		shadow.innerHTML = /* HTML */ `
			<style>${CarouselV2.styles}</style>
			${this.hasAttribute('arrows-desktop-only') ? /* HTML */ `
				<style>
					@media screen and (max-width: 991px) {
						.arrow {
							display: none;
						}
					}
				</style>
			` : ''}
			<div class="content">
				${this.arrows ? carouselArrow(true) : ''}
				<div class="items">
					<slot></slot>
				</div>
				${this.arrows ? carouselArrow() : ''}
				${this.hasAttribute('scrollbar') ? /* HTML */ `<fake-scrollbar target=".items"></fake-scrollbar>` : ''}
			</div>
		`;

		this.items = this.shadowRoot.querySelector('.items');
		this.content = this.shadowRoot.querySelector('.content');

		// Add arrow click handlers
		if (this.arrows) {
			this.prevArrow = this.shadowRoot.querySelector('.arrow--prev');
			this.nextArrow = this.shadowRoot.querySelector('.arrow--next');

			this.nextArrow.addEventListener('click', e => {
				this.items.scrollBy({ left: this.#calculateScrollAmount(), behavior: 'smooth' });
			});

			this.prevArrow.addEventListener('click', e => {
				this.items.scrollBy({ left: -this.#calculateScrollAmount(), behavior: 'smooth' });
			});

			this.content.addEventListener('mousemove', e => {
				const { left, right } = this.content.getBoundingClientRect();
				this.content.classList.toggle('hover-left', e.clientX - left < 100);
				this.content.classList.toggle('hover-right', right - e.clientX < 100);
			}, { passive: true });

			this.content.addEventListener('mouseleave', e => this.content.classList.remove('hover-left', 'hover-right'));
		}

		// Add desktop dragging support
		this.#addDragHandlers();
	
		if (this.hasAttribute('scrollbar')) this.scrollbar = this.shadowRoot.querySelector('fake-scrollbar');
		this.itemWidth = 0;

		this.index = 0;
		this.indexUpdateHandler = null;
		
		const slot = this.shadowRoot.querySelector('slot');
		slot.addEventListener('slotchange', () => this.#onSlotChange());

		this.items.addEventListener('scroll', () => this.#onScroll());

		// Update item width and scroll bar on resize
		const resizeObserver = new ResizeObserver(() => this.#onResize());
		resizeObserver.observe(this);

		if (this.autoplay) this.#startAutoplay();

		this.setAttribute('init', '');
	}

	#addDragHandlers() {
		let dragging = false;
		let mouseStart = 0;
		let scrollStart = 0;

		this.items.addEventListener('mousedown', e => {
			if (e.button == 0 && window.matchMedia("(hover: hover)").matches) {
				dragging = true;
				mouseStart = e.clientX;
				scrollStart = this.items.scrollLeft;
				this.items.style.scrollSnapType = 'none';
			}
		});

		this.items.addEventListener('click', e => {
			if (this.hasAttribute('dragging')) {
				e.preventDefault();
				e.stopPropagation();
			}
		}, { capture: true });

		window.addEventListener('mousemove', e => {
			if (dragging) {
				this.items.scrollLeft = scrollStart - (e.clientX - mouseStart);
				if (!this.hasAttribute('dragging')) this.setAttribute('dragging', '');
			}
		});

		window.addEventListener('mouseup', e => {
			if (dragging) {
				dragging = false;
				this.#updateIndex();
				this.scrollToIndex(this.index);
	
				setTimeout(() => {
					this.items.style.scrollSnapType = '';
					this.removeAttribute('dragging');
				}, 300);
			}
		});
	}

	#calculateScrollAmount() {
		const itemWidth = this.firstElementChild.offsetWidth;
		return Math.floor(this.items.offsetWidth / itemWidth) * itemWidth;
	}

	#onSlotChange() {
		for (const draggable of this.querySelectorAll('img, a')) draggable.draggable = false;
		this.content.style.setProperty('--item-count', this.children.length);
		this.scrollToIndex(0, false);

		if (this.infinite) {
			const firstImage = this.querySelector(':scope > *:first-child img, :scope > img:first-child');
			const lastImage = this.querySelector(':scope > *:last-child img, :scope > img:last-child');

			if (firstImage && lastImage) {
				this.items.style.setProperty('--infinite-bg-start', `url(${lastImage.src})`);
				this.items.style.setProperty('--infinite-bg-end', `url(${firstImage.src})`);
			}
		}
	}

	#onScroll() {
		if (this.infinite) {
			if (this.items.scrollLeft >= this.items.scrollWidth - this.items.clientWidth - 4) {
				this.setAttribute('jump', '');
				setTimeout(() => this.removeAttribute('jump'), 200);

				this.items.scrollLeft = this.itemWidth;
			}
			else if (this.items.scrollLeft == 0) {			
				const gap = parseInt(getComputedStyle(this.items).gap.replace('px', ''));
				this.setAttribute('jump', '');
				setTimeout(() => this.removeAttribute('jump'), 200);

				this.items.scrollLeft = this.items.scrollWidth - this.items.clientWidth - this.itemWidth - gap;
			}
		}

		if (this.arrows) {
			if (this.items.scrollLeft < 4) this.prevArrow.setAttribute('disabled', '');
			else this.prevArrow.removeAttribute('disabled');

			if (this.items.scrollLeft >= this.items.scrollWidth - this.items.clientWidth - 4) this.nextArrow.setAttribute('disabled', '');
			else this.nextArrow.removeAttribute('disabled');
		}

		if ((this.events || this.autoplay) && !this.indexUpdateHandler) {
			this.indexUpdateHandler = setTimeout(() => {
				this.#updateIndex();
				this.indexUpdateHandler = null;
			}, 1000);
		}
	}

	#updateIndex() {
		let index = 0;

		if (this.items.scrollLeft >= this.items.scrollWidth - this.items.offsetWidth - this.lastElementChild.offsetWidth / 4) {
			index = this.children.length - 1;
		}
		else {
			for (const [idx, child] of [...this.children].entries()) {
				if (child.offsetLeft + child.offsetWidth / 2 >= this.items.scrollLeft) {
					index = idx;
					break;
				}
			}
		}

		if (this.index !== index) {
			this.index = index;
			if (this.events) this.dispatchEvent(new CustomEvent('cardchange', { detail: { index: this.index } }));
		}
	}

	#onResize() {
		if (this.infinite) this.itemWidth = this.firstElementChild.offsetWidth;
		this.scrollbar?.setAttribute('margin', this.infinite ? this.itemWidth * 2 : 0);
		this.#onScroll();
	}

	#startAutoplay() {
		const interval = parseInt(this.getAttribute('interval')) || 5000;
		
		this.autoplayHandle = setInterval(() => {
			const current = this.children[this.index];

			this.autoplayRunning = true;
			this.items.style.scrollSnapType = 'none';
			
			const nextIndex = this.index + 1;
			if (nextIndex >= this.children.length) this.scrollToIndex(0, false);
			else this.scrollToIndex(nextIndex, false);

			current.style.transform = `translateX(${this.items.scrollLeft - current.offsetLeft}px)`;
			current.style.animation = 'card-fade-in 500ms both reverse';
			current.style.zIndex = 50;

			setTimeout(() => {
				current.style.transform = '';
				current.style.animation = '';
				current.style.zIndex = '';

				this.autoplayRunning = false;
				this.items.style.scrollSnapType = '';
			}, 500);
		}, interval);

		const cancelOnScroll = () => {
			if (this.autoplayHandle && !this.autoplayRunning) {
				clearInterval(this.autoplayHandle);
				this.autoplayHandle = null;
				this.items.removeEventListener('scroll', cancelOnScroll);
			}
		}

		this.items.addEventListener('scroll', cancelOnScroll);
	}

	scrollToIndex(index, smooth = true) {
		const item = this.children[index];
		if (item) this.items.scrollTo({ left: item.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
	}

	static styles = /* CSS */ `
		* {
			box-sizing: border-box;
		}

		:host {
			display: block;
			overflow: initial;
			position: relative;
		}

		.content {
			position: relative;
		}

		.items {
			display: flex;
			gap: var(--gap-value);
			overflow-x: auto;
			overflow-y: hidden;
			scrollbar-width: none;
  		-ms-overflow-style: none;
			scroll-snap-type: x mandatory;
			padding-inline: var(--padding-value);
			margin-inline: var(--margin-value);
			scroll-padding-inline: var(--padding-value);
		}

		.items::-webkit-scrollbar,
		.items::-webkit-scrollbar {
			width: 0;
			height: 0;
			display: none;
		}

		.items::before,
		.items::after {
			content: '';
			display: none;
			aspect-ratio: 0.8;
			flex: 1 0 100%;
			width: 100%;
			height: auto;
			background-size: contain;
			background-repeat: no-repeat
		}

		::slotted(*),
		.items::before,
		.items::after {
			flex: 0 0 var(--item-width-value);
			min-width: 0;
			scroll-snap-align: start;
		}

		:host([scrollbar="floating"]) fake-scrollbar {
			bottom: 10px;
			border-radius: 2px;
			overflow: hidden;
			max-width: calc(var(--item-count) * 60px);
			left: 50%;
			transform: translateX(-50%);
			filter: drop-shadow(0px 1px 2px rgba(0,0,0,.14));
		}

		@media screen and (min-width: 992px) {
			:host([scrollbar="mobile"]) fake-scrollbar {
				display: none;
			}
		}

		:host([dragging]) ::slotted(*) {
			pointer-events: none;
		}

		:host([jump]) ::slotted(:nth-child(2)),
		:host([jump]) ::slotted(:nth-last-child(2))  {
			animation: card-fade-in 200ms both;
		}

		:host([infinite]) .items::before,
		:host([infinite]) .items::after {
			display: block;
		}

		.items::before {
			background-image: var(--infinite-bg-start);
		}

		.items::after {
			background-image: var(--infinite-bg-end);
		}	

		.arrow {
			background: transparent;
			border: none;
			padding: 0;  
			position: absolute;
			top: 50%;
			width: 40px;
			height: 40px;
			transition: opacity 150ms, background-color 250ms;
			cursor: pointer;
			z-index: 100;
			margin-top: var(--arrow-offset, 0);
		}

		.arrow[disabled] {
			opacity: 0% !important;
			pointer-events: none;
		}

		.arrow:active {
			filter: brightness(90%);
		}

		.arrow svg {
			width: 100%;
			height: auto;
		}

		.arrow--prev {
			left: 0;
			transform: translateY(-50%) scaleX(-1);
		}

		.arrow--next {
			right: 0;
			transform: translateY(-50%);
		}

		@media (hover: hover) {
			.arrow {
				opacity: 0.9;
			}

			.arrow:hover {
				opacity: 1;
			}

			.hover-left .arrow--prev,
			.hover-right .arrow--next {
				background-color: var(--colours-alpha-light-40, rgba(255, 255, 255, 0.60));
			}
		}
	`;
}

customElements.define('carousel-v2', CarouselV2);